var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from "react";
import { useLocale } from "@aviv-unified-frontend/runtime/i18n";
import { useSnackbar } from "@gemini/ui";
import { useConfig, useCrsBaseUrl } from "@/contexts/cdpConfig";
import { useClassified } from "@/contexts/classified";
import { useConfirmationPage } from "@/contexts/confirmPage";
import { useCdpId, useContactBrand, useContactContext, } from "@/contexts/contact";
import { useI18n } from "@/shared/hooks";
import { useCustomerAction, useSendLeadTracking } from "@/hooks";
import { useClassifiedAnalytics } from "@/hooks/tracking/classifiedAnalytics";
import { useIsConfirmationPageDisabled } from "@/components/contacting/hooks/useIsConfirmationPageDisabled";
import { useTrackingLegacyLeadEvents } from "./tracking/trackingLegacy";
import { CA_EVENT_TYPES } from "@/types/classifiedAnalytics";
import { buildSendMailActionParams, sendLead } from "@/utils";
import { useAuthentication } from "@aviv-unified-frontend/runtime/authentication";
// Hook to send multiple leads in sequence
// Reuses logic from useSendLead but overrides classifiedId for each request.
export const useSendMultipleLeads = () => {
    var _a, _b;
    const { t } = useI18n();
    const { language } = useLocale();
    const { add: addSnackbar } = useSnackbar();
    const { unsafe_accessToken } = useAuthentication();
    const { customerActionExposeContact } = useCustomerAction();
    const { sendClassifiedAnalyticsEvent } = useClassifiedAnalytics();
    const { triggerConfirmView, setPartnerTitles } = useConfirmationPage();
    const { closeMultiInquiryModal } = useContactContext();
    const { leadFormOk, leadFormConfirm, getAdditionalProps } = useSendLeadTracking();
    const brand = useContactBrand();
    const cdpLegacyId = useCdpId();
    const baseUrl = useCrsBaseUrl();
    const sendLeadUrl = `${baseUrl}/contacting`;
    const featuresFlags = (_a = useConfig()) === null || _a === void 0 ? void 0 : _a.featuresFlags;
    const isConfirmationPageDisabled = useIsConfirmationPageDisabled();
    const { legacyLeadConversionMail } = useTrackingLegacyLeadEvents(); //legacy GA3 for harvest
    const legacyTrackingData = (_b = useClassified()) === null || _b === void 0 ? void 0 : _b.legacyTracking;
    const hasLegacyTrackingData = !!legacyTrackingData;
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    // Send leads for multiple classifiedIds in series.
    const sendMultipleLeads = (_a) => __awaiter(void 0, [_a], void 0, function* ({ formData, selectedListingIds, selectedListings, av_zone, }) {
        setLoading(true);
        setErrors([]);
        for (const classifiedId of selectedListingIds) {
            const currentListing = selectedListings.find((item) => item.tracking.id === classifiedId);
            const trackingProps = getAdditionalProps(formData, av_zone);
            const currentListingTracking = currentListing === null || currentListing === void 0 ? void 0 : currentListing.tracking;
            const trackingPayload = Object.assign(Object.assign({}, trackingProps), { eventData: { av_items: [currentListingTracking] } });
            if (hasLegacyTrackingData) {
                const product = Object.assign(Object.assign({}, currentListingTracking), { list_name: "multi-inquiry" });
                legacyLeadConversionMail({
                    eventData: {
                        infos: "multi-inquiry",
                        email_lead: formData === null || formData === void 0 ? void 0 : formData.email,
                        products: [product],
                    },
                });
            }
            leadFormOk(trackingPayload);
            const payload = Object.assign(Object.assign({}, formData), { language, classifiedId: classifiedId, gokId: cdpLegacyId, brand,
                featuresFlags });
            try {
                const response = yield sendLead(sendLeadUrl, payload, unsafe_accessToken);
                if (response.status !== 200) {
                    throw new Error(`Error sending lead for ${classifiedId}`);
                }
                leadFormConfirm(Object.assign(Object.assign({}, trackingProps), { eventData: {
                        email_lead: formData === null || formData === void 0 ? void 0 : formData.email,
                    } }));
                sendClassifiedAnalyticsEvent(CA_EVENT_TYPES.CONTACT_FORM_SUBMIT, classifiedId);
                yield customerActionExposeContact(buildSendMailActionParams(formData));
            }
            catch (err) {
                console.error(`Error sending lead for ${classifiedId}:`, err);
                setErrors((prev) => [...prev, classifiedId]);
            }
        }
        setLoading(false);
        setPartnerTitles(selectedListings.map((item) => item.provider.contactCard.title));
        if (isConfirmationPageDisabled) {
            addSnackbar({
                variant: "info",
                description: t("cdpSeeker_contactForm_success_message"),
            });
            closeMultiInquiryModal();
        }
        else {
            triggerConfirmView("multi");
        }
    });
    return { sendMultipleLeads, loading, errors };
};
